/**
 * 分项报价编辑器组件
 * 支持按阶段分组、增删行、拖动排序、自动计算总价、超支警示
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { PlusIcon, TrashIcon, CalculatorIcon, ExclamationTriangleIcon, PencilIcon, BoltIcon } from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 分项报价项
export interface QuotationItem {
  id: string;
  category?: string; // 阶段/类别
  name: string;
  description: string;
  unit: string;
  quantity: number;
  unitPrice: number | null;
  isRequired: boolean;
  sortOrder?: number;
}

// 报价数据
export interface QuotationData {
  items: QuotationItem[];
  maxTotalPrice: number | null;
  warningPercent: number;
  notes: string;
}

interface QuotationEditorProps {
  initialData?: QuotationData;
  onChange?: (data: QuotationData) => void;
  readOnly?: boolean;
}

const defaultData: QuotationData = {
  items: [],
  maxTotalPrice: null,
  warningPercent: 90,
  notes: ''
};

// 可排序的行组件
interface SortableItemProps {
  item: QuotationItem;
  index: number;
  readOnly: boolean;
  onUpdate: (id: string, field: keyof QuotationItem, value: any) => void;
  onDelete: (id: string) => void;
  formatAmount: (amount: number) => string;
  calculateSubtotal: (item: QuotationItem) => number;
  uniqueUnits: string[];
}

const SortableItem: React.FC<SortableItemProps> = ({
  item,
  index,
  readOnly,
  onUpdate,
  onDelete,
  formatAmount,
  calculateSubtotal,
  uniqueUnits,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const [editingName, setEditingName] = useState(false);
  const [editingUnit, setEditingUnit] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [tempName, setTempName] = useState(item.name);
  const [tempUnit, setTempUnit] = useState(item.unit);
  const [tempDescription, setTempDescription] = useState(item.description);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // 保存名称
  const saveName = () => {
    onUpdate(item.id, 'name', tempName);
    setEditingName(false);
  };

  // 取消编辑名称
  const cancelEditName = () => {
    setTempName(item.name);
    setEditingName(false);
  };

  // 保存单位
  const saveUnit = () => {
    onUpdate(item.id, 'unit', tempUnit);
    setEditingUnit(false);
  };

  // 取消编辑单位
  const cancelEditUnit = () => {
    setTempUnit(item.unit);
    setEditingUnit(false);
  };

  // 保存描述
  const saveDescription = () => {
    onUpdate(item.id, 'description', tempDescription);
    setEditingDescription(false);
  };

  // 取消编辑描述
  const cancelEditDescription = () => {
    setTempDescription(item.description);
    setEditingDescription(false);
  };

  // 处理按键
  const handleKeyDown = (e: React.KeyboardEvent, save: () => void, cancel: () => void) => {
    if (e.key === 'Enter') {
      save();
    } else if (e.key === 'Escape') {
      cancel();
    }
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`group ${isDragging ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
    >
      {/* 拖动句柄 - 悬浮显示 */}
      {!readOnly && (
        <td className="px-2 py-3 border-b border-gray-200">
          <div
            {...attributes}
            {...listeners}
            className="cursor-move text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
            title="拖动排序"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM11.5 15.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z" />
            </svg>
          </div>
        </td>
      )}
      
      {/* 序号 */}
      <td className="px-3 py-3 border-b border-gray-200 text-center text-gray-600">
        {index + 1}
      </td>
      
      {/* 分项名称 */}
      <td className="px-3 py-3 border-b border-gray-200">
        {editingName ? (
          <input
            type="text"
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            onBlur={saveName}
            onKeyDown={(e) => handleKeyDown(e, saveName, cancelEditName)}
            autoFocus
            className="w-full px-2 py-1 text-sm bg-white/60 rounded border-0 outline-none focus:bg-white focus:ring-0"
            placeholder="分项名称"
          />
        ) : (
          <div 
            className="inline-flex items-center cursor-pointer group"
            onClick={() => {
              if (!readOnly) {
                setTempName(item.name);
                setEditingName(true);
              }
            }}
          >
            <span className={`text-sm ${item.name ? 'text-gray-800' : 'text-gray-400 italic'}`}>
              {item.name || '点击输入名称'}
            </span>
            {!readOnly && (
              <PencilIcon className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
            )}
          </div>
        )}
        {editingDescription ? (
          <textarea
            value={tempDescription}
            onChange={(e) => setTempDescription(e.target.value)}
            onBlur={saveDescription}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                saveDescription();
              } else if (e.key === 'Escape') {
                cancelEditDescription();
              }
            }}
            autoFocus
            rows={4}
            className="w-full mt-1 px-2 py-2 text-sm bg-white/60 rounded border-0 outline-none focus:bg-white focus:ring-0 resize-none"
            placeholder="点击输入描述"
          />
        ) : (
          <div 
            className="inline-flex items-start cursor-pointer group mt-1"
            onClick={() => {
              if (!readOnly) {
                setTempDescription(item.description);
                setEditingDescription(true);
              }
            }}
          >
            <span className={`text-xs ${item.description ? 'text-gray-500' : 'text-gray-400 italic'}`}>
              {item.description || '点击输入描述'}
            </span>
            {!readOnly && (
              <PencilIcon className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1 flex-shrink-0 mt-0.5" />
            )}
          </div>
        )}
      </td>
      
      {/* 单位 */}
      <td className="px-3 py-3 border-b border-gray-200 text-center">
        {editingUnit ? (
          <input
            type="text"
            value={tempUnit}
            onChange={(e) => setTempUnit(e.target.value)}
            onBlur={saveUnit}
            onKeyDown={(e) => handleKeyDown(e, saveUnit, cancelEditUnit)}
            autoFocus
            className="w-16 px-1 py-1 text-sm text-center bg-white/60 rounded border-0 outline-none focus:bg-white focus:ring-0"
            placeholder="单位"
          />
        ) : (
          <div 
            className="inline-flex items-center cursor-pointer group"
            onClick={() => {
              if (!readOnly) {
                setTempUnit(item.unit);
                setEditingUnit(true);
              }
            }}
          >
            <span className={`text-sm ${item.unit ? 'text-gray-800' : 'text-gray-400 italic'}`}>
              {item.unit || '-'}
            </span>
            {!readOnly && (
              <PencilIcon className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
            )}
          </div>
        )}
      </td>
      
      {/* 数量 */}
      <td className="px-3 py-3 border-b border-gray-200">
        <div className="flex items-center justify-center">
          <button
            onClick={() => onUpdate(item.id, 'quantity', Math.max(0, item.quantity - 1))}
            disabled={readOnly || item.quantity <= 0}
            className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-30"
          >
            -
          </button>
          <input
            type="text"
            inputMode="decimal"
            value={item.quantity}
            onChange={(e) => {
              const val = parseFloat(e.target.value.replace(/[^\d.]/g, ''));
              onUpdate(item.id, 'quantity', isNaN(val) ? 0 : Math.max(0, val));
            }}
            disabled={readOnly}
            className="w-16 px-1 py-1 text-center text-sm bg-transparent border-0 outline-none focus:ring-0"
          />
          <button
            onClick={() => onUpdate(item.id, 'quantity', item.quantity + 1)}
            disabled={readOnly}
            className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          >
            +
          </button>
        </div>
      </td>
      
      {/* 单价 */}
      <td className="px-3 py-3 border-b border-gray-200">
        <input
          type="text"
          inputMode="decimal"
          value={item.unitPrice !== null && item.unitPrice !== undefined ? item.unitPrice.toFixed(2) : ''}
          onChange={(e) => {
            const val = e.target.value.replace(/[^\d.]/g, '');
            onUpdate(item.id, 'unitPrice', val ? parseFloat(val) : null);
          }}
          disabled={readOnly}
          placeholder="待填写"
          className={`w-28 px-2 py-1 border rounded text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
            item.isRequired && item.unitPrice === null ? 'border-red-300 bg-red-50' : 'border-gray-300'
          }`}
        />
      </td>
      
      {/* 小计 */}
      <td className="px-3 py-3 border-b border-gray-200 text-right font-medium text-gray-700">
        {item.unitPrice !== null ? formatAmount(calculateSubtotal(item)) : '-'}
      </td>
      
      {/* 操作 */}
      {!readOnly && (
        <td className="px-3 py-3 border-b border-gray-200">
          <button
            onClick={() => onDelete(item.id)}
            className="text-red-500 hover:text-red-700 transition-colors"
            title="删除"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </td>
      )}
    </tr>
  );
};

const QuotationEditor: React.FC<QuotationEditorProps> = ({
  initialData,
  onChange,
  readOnly = false
}) => {
  const [data, setData] = useState<QuotationData>(initialData || defaultData);
  const [editingMaxPrice, setEditingMaxPrice] = useState(false);
  const [tempMaxPrice, setTempMaxPrice] = useState<string>('');
  const [activeId, setActiveId] = useState<string | null>(null);

  // 传感器配置
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 监听 initialData 变化
  useEffect(() => {
    if (initialData) {
      setData(initialData);
    }
  }, [initialData]);

  // 按类别分组的项目
  const groupedItems = useMemo(() => {
    const groups: { [key: string]: QuotationItem[] } = {};
    data.items.forEach(item => {
      const category = item.category || '未分类';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });
    return groups;
  }, [data.items]);

  // 获取所有类别
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    data.items.forEach(item => {
      const cat = item.category || '未分类';
      if (!seen.has(cat)) {
        seen.add(cat);
        result.push(cat);
      }
    });
    return result;
  }, [data.items]);

  // 获取所有唯一的单位
  const uniqueUnits = useMemo(() => {
    const units = new Set<string>();
    data.items.forEach(item => {
      if (item.unit) {
        units.add(item.unit);
      }
    });
    return Array.from(units);
  }, [data.items]);

  // 自动计算总价
  const totalPrice = useMemo(() => {
    return data.items.reduce((sum, item) => {
      return sum + (item.quantity * (item.unitPrice || 0));
    }, 0);
  }, [data.items]);

  // 计算已填写的项数
  const filledCount = useMemo(() => {
    return data.items.filter(item => item.unitPrice !== null && item.unitPrice > 0).length;
  }, [data.items]);

  // 警示状态
  const warningStatus = useMemo(() => {
    if (!data.maxTotalPrice || data.maxTotalPrice <= 0) return 'normal';
    const ratio = (totalPrice / data.maxTotalPrice) * 100;
    if (ratio > 100) return 'danger';
    if (ratio >= data.warningPercent) return 'warning';
    return 'normal';
  }, [totalPrice, data.maxTotalPrice, data.warningPercent]);

  // 剩余可用金额
  const remainingAmount = useMemo(() => {
    if (!data.maxTotalPrice) return null;
    return data.maxTotalPrice - totalPrice;
  }, [data.maxTotalPrice, totalPrice]);

  // 更新数据并通知父组件
  const updateData = useCallback((newData: QuotationData) => {
    setData(newData);
    onChange?.(newData);
  }, [onChange]);

  // 添加新行
  const addItem = useCallback((category?: string) => {
    const newItem: QuotationItem = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      category: category,
      name: '',
      description: '',
      unit: '项',
      quantity: 1,
      unitPrice: null,
      isRequired: true,
    };
    updateData({
      ...data,
      items: [...data.items, newItem]
    });
  }, [data, updateData]);

  // 删除行
  const deleteItem = useCallback((id: string) => {
    updateData({
      ...data,
      items: data.items.filter(item => item.id !== id)
    });
  }, [data, updateData]);

  // 更新单元格
  const updateItem = useCallback((id: string, field: keyof QuotationItem, value: any) => {
    updateData({
      ...data,
      items: data.items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  }, [data, updateData]);

  // 更新全局设置
  const updateSetting = useCallback((field: keyof QuotationData, value: any) => {
    updateData({
      ...data,
      [field]: value
    });
  }, [data, updateData]);

  // 拖动排序处理
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = data.items.findIndex(item => item.id === active.id);
      const newIndex = data.items.findIndex(item => item.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newItems = arrayMove(data.items, oldIndex, newIndex);
        updateData({ ...data, items: newItems });
      }
    }
    setActiveId(null);
  };

  // 开始编辑最高限价
  const startEditMaxPrice = () => {
    setTempMaxPrice(data.maxTotalPrice?.toString() || '');
    setEditingMaxPrice(true);
  };

  // 保存最高限价
  const saveMaxPrice = () => {
    const value = tempMaxPrice ? parseFloat(tempMaxPrice) : null;
    updateSetting('maxTotalPrice', value);
    setEditingMaxPrice(false);
  };

  // 取消编辑最高限价
  const cancelEditMaxPrice = () => {
    setEditingMaxPrice(false);
    setTempMaxPrice('');
  };

  // 处理按键
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveMaxPrice();
    } else if (e.key === 'Escape') {
      cancelEditMaxPrice();
    }
  };

  // 计算单项小计
  const calculateSubtotal = (item: QuotationItem) => {
    return item.quantity * (item.unitPrice || 0);
  };

  // 格式化金额
  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // 获取当前拖动的项
  const activeItem = useMemo(() => {
    return activeId ? data.items.find(item => item.id === activeId) : null;
  }, [activeId, data.items]);

  // ===== 智能报价弹窗状态 =====
  const [showSmartPricing, setShowSmartPricing] = useState(false);
  const [smartPricingAmount, setSmartPricingAmount] = useState<string>('');
  const [smartPricingRoundPrice, setSmartPricingRoundPrice] = useState(false);
  const smartPricingInputRef = useRef<HTMLInputElement>(null);

  // 打开智能报价弹窗
  const openSmartPricing = useCallback(() => {
    if (data.items.length === 0) return;
    // 默认填入最高限价（如果有的话）
    setSmartPricingAmount(data.maxTotalPrice ? data.maxTotalPrice.toString() : '');
    setShowSmartPricing(true);
    setTimeout(() => smartPricingInputRef.current?.select(), 100);
  }, [data.items.length, data.maxTotalPrice]);

  // 执行智能报价分配
  const executeSmartPricing = useCallback(() => {
    const targetTotal = parseFloat(smartPricingAmount);
    if (!targetTotal || targetTotal <= 0 || data.items.length === 0) return;

    const validItems = data.items.filter(item => item.quantity > 0);
    if (validItems.length === 0) return;

    const roundFn = (val: number) => smartPricingRoundPrice ? Math.round(val) : Math.round(val * 100) / 100;

    // 分配策略：如果已有单价的项，按已有单价比例分配；否则按数量均分
    const hasExistingPrices = validItems.some(item => item.unitPrice !== null && item.unitPrice > 0);

    let newItems: QuotationItem[];

    if (hasExistingPrices) {
      const currentTotal = validItems.reduce((sum, item) => sum + item.quantity * (item.unitPrice || 0), 0);
      if (currentTotal > 0) {
        const ratio = targetTotal / currentTotal;
        newItems = data.items.map(item => {
          if (item.quantity <= 0) return item;
          return { ...item, unitPrice: roundFn((item.unitPrice || 0) * ratio) };
        });
      } else {
        const avgPerUnit = targetTotal / validItems.reduce((sum, item) => sum + item.quantity, 0);
        newItems = data.items.map(item => {
          if (item.quantity <= 0) return item;
          return { ...item, unitPrice: roundFn(avgPerUnit) };
        });
      }
    } else {
      const totalQuantity = validItems.reduce((sum, item) => sum + item.quantity, 0);
      const avgPerUnit = targetTotal / totalQuantity;
      newItems = data.items.map(item => {
        if (item.quantity <= 0) return item;
        return { ...item, unitPrice: roundFn(avgPerUnit) };
      });
    }

    // 修正尾差：调整最后一个有效项的单价，确保总额精确匹配
    const currentSum = newItems.reduce((sum, item) => sum + item.quantity * (item.unitPrice || 0), 0);
    const diff = targetTotal - currentSum;
    if (Math.abs(diff) > 0.001) {
      for (let i = newItems.length - 1; i >= 0; i--) {
        if (newItems[i].quantity > 0 && newItems[i].unitPrice !== null) {
          const adjustedPrice = (newItems[i].unitPrice! * newItems[i].quantity + diff) / newItems[i].quantity;
          newItems[i] = { ...newItems[i], unitPrice: roundFn(adjustedPrice) };
          break;
        }
      }
    }

    updateData({ ...data, items: newItems });
    setShowSmartPricing(false);
    setSmartPricingAmount('');
  }, [smartPricingAmount, data, updateData, smartPricingRoundPrice]);

  return (
    <div>
      {/* 预算控制面板 - 粘性定位 */}
      <div className="sticky top-0 z-20 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100 shadow-sm mt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <CalculatorIcon className="w-5 h-5 mr-2 text-blue-600" />
            预算控制
          </h3>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              已填写: <span className="font-semibold text-blue-600">{filledCount}</span> / {data.items.length} 项
            </span>
          </div>
        </div>

        {/* 四个卡片面板 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* 最高限价 - 可编辑 */}
          <div className={`relative rounded-xl p-4 ${
            data.maxTotalPrice 
              ? warningStatus === 'danger' 
                ? 'bg-red-100 border-2 border-red-300' 
                : warningStatus === 'warning'
                  ? 'bg-yellow-100 border-2 border-yellow-300'
                  : 'bg-green-100 border-2 border-green-300'
              : 'bg-gray-100 border-2 border-gray-300'
          }`}>
            <div className="text-sm font-medium text-gray-600 mb-1">最高限价</div>
            {editingMaxPrice ? (
              <input
                type="text"
                inputMode="decimal"
                value={tempMaxPrice}
                onChange={(e) => setTempMaxPrice(e.target.value.replace(/[^\d.]/g, ''))}
                onBlur={saveMaxPrice}
                onKeyDown={handleKeyDown}
                autoFocus
                className="w-full px-2 py-1 text-lg font-bold bg-white/60 rounded border-0 outline-none focus:bg-white focus:ring-0"
                placeholder="输入金额"
              />
            ) : (
              <div 
                className="flex items-center cursor-pointer group"
                onClick={startEditMaxPrice}
              >
                <span className={`text-lg font-bold ${
                  data.maxTotalPrice ? 'text-gray-800' : 'text-gray-400 italic'
                }`}>
                  {data.maxTotalPrice ? formatAmount(data.maxTotalPrice) : '待设置'}
                </span>
                <PencilIcon className="w-4 h-4 text-gray-500 ml-2 opacity-70 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </div>

          {/* 当前总价 */}
          <div className={`rounded-xl p-4 ${
            warningStatus === 'danger' 
              ? 'bg-red-100 border-2 border-red-300' 
              : warningStatus === 'warning'
                ? 'bg-yellow-100 border-2 border-yellow-300'
                : 'bg-blue-100 border-2 border-blue-300'
          }`}>
            <div className="text-sm font-medium text-gray-600 mb-1">当前报价</div>
            <div className="text-lg font-bold text-gray-800">
              {formatAmount(totalPrice)}
            </div>
          </div>

          {/* 当前占比 */}
          <div className={`rounded-xl p-4 ${
            warningStatus === 'danger' 
              ? 'bg-red-100 border-2 border-red-300' 
              : warningStatus === 'warning'
                ? 'bg-yellow-100 border-2 border-yellow-300'
                : 'bg-purple-100 border-2 border-purple-300'
          }`}>
            <div className="text-sm font-medium text-gray-600 mb-1">报价占比</div>
            <div className="text-lg font-bold text-gray-800">
              {data.maxTotalPrice && data.maxTotalPrice > 0
                ? `${((totalPrice / data.maxTotalPrice) * 100).toFixed(1)}%`
                : '-'
              }
            </div>
          </div>

          {/* 剩余金额 */}
          <div className={`rounded-xl p-4 ${
            warningStatus === 'danger' 
              ? 'bg-red-100 border-2 border-red-300' 
              : warningStatus === 'warning'
                ? 'bg-yellow-100 border-2 border-yellow-300'
                : remainingAmount !== null && remainingAmount < 0
                  ? 'bg-red-100 border-2 border-red-300'
                  : 'bg-emerald-100 border-2 border-emerald-300'
          }`}>
            <div className="text-sm font-medium text-gray-600 mb-1">剩余金额</div>
            <div className={`text-lg font-bold ${
              remainingAmount !== null && remainingAmount < 0 ? 'text-red-700' : 'text-gray-800'
            }`}>
              {remainingAmount !== null ? formatAmount(remainingAmount) : '-'}
            </div>
          </div>
        </div>

        {/* 警示信息 */}
        {warningStatus === 'danger' && (
          <div className="mt-4 flex items-center text-red-600 bg-red-50 px-4 py-2 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
            <span className="font-medium">已超出预算限制！</span>
          </div>
        )}
        {warningStatus === 'warning' && (
          <div className="mt-4 flex items-center text-yellow-600 bg-yellow-50 px-4 py-2 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
            <span>已接近预算上限，剩余可用: {formatAmount(remainingAmount || 0)}</span>
          </div>
        )}
      </div>

      {/* 分项明细 - 按阶段分组 */}
      <div className="mt-6">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(e) => setActiveId(e.active.id as string)}
          onDragEnd={handleDragEnd}
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">分项明细</h3>
            {!readOnly && (
              <div className="flex items-center space-x-2">
                {data.items.length > 0 && (
                  <button
                    onClick={openSmartPricing}
                    className="flex items-center px-3 py-1.5 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition-colors"
                  >
                    <BoltIcon className="w-4 h-4 mr-1" />
                    智能报价
                  </button>
                )}
                <button
                  onClick={() => addItem()}
                  className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  添加分项
                </button>
              </div>
            )}
          </div>

          {categories.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <CalculatorIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>暂无报价项，点击"添加分项"或"智能提取"开始</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {categories.map((category) => (
                <div key={category} className="bg-white">
                  {/* 阶段标题 */}
                  <div className="px-5 py-3 bg-gray-100 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-700">{category}</h4>
                  </div>
                  
                  {/* 该阶段的表格 */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          {!readOnly && <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase w-10"></th>}
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-12">序号</th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">分项名称</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">单位</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">数量</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-32">单价（元）</th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase w-32">小计</th>
                          {!readOnly && <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-16">操作</th>}
                        </tr>
                      </thead>
                      <SortableContext
                        items={groupedItems[category]?.map(item => item.id) || []}
                        strategy={verticalListSortingStrategy}
                      >
                        <tbody className="divide-y divide-gray-100">
                          {groupedItems[category]?.map((item, idx) => (
                            <SortableItem
                              key={item.id}
                              item={item}
                              index={idx}
                              readOnly={readOnly}
                              onUpdate={updateItem}
                              onDelete={deleteItem}
                              formatAmount={formatAmount}
                              calculateSubtotal={calculateSubtotal}
                              uniqueUnits={uniqueUnits}
                            />
                          ))}
                        </tbody>
                      </SortableContext>
                    </table>
                  </div>
                  
                  {/* 该阶段添加按钮 */}
                  {!readOnly && (
                    <div className="px-5 py-2 bg-gray-50 border-t border-gray-200">
                      <button
                        onClick={() => addItem(category)}
                        className="flex items-center text-sm text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <PlusIcon className="w-4 h-4 mr-1" />
                        在"{category}"下添加分项
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 拖动时的遮罩层 */}
        <DragOverlay>
          {activeItem ? (
            <div className="bg-white shadow-xl rounded-lg border-2 border-blue-400 p-4 opacity-90">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM10 8.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM11.5 15.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z" />
                </svg>
                <span className="font-medium text-gray-800">{activeItem.name || '未命名分项'}</span>
                <span className="text-sm text-gray-500">{activeItem.unit} × {activeItem.quantity}</span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
        </DndContext>
      </div>

      {/* 智能报价弹窗 */}
      {showSmartPricing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSmartPricing(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[440px] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* 弹窗头部 */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
              <h3 className="text-lg font-bold text-white flex items-center">
                <BoltIcon className="w-5 h-5 mr-2" />
                智能报价
              </h3>
              <p className="text-amber-100 text-sm mt-1">
                输入目标总金额，系统将自动分配各分项单价
              </p>
            </div>
            {/* 弹窗内容 */}
            <div className="px-6 py-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                目标报价总金额（元）
              </label>
              <input
                ref={smartPricingInputRef}
                type="text"
                inputMode="decimal"
                value={smartPricingAmount}
                onChange={(e) => setSmartPricingAmount(e.target.value.replace(/[^\d.]/g, ''))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') executeSmartPricing();
                  if (e.key === 'Escape') setShowSmartPricing(false);
                }}
                placeholder="请输入总金额"
                className="w-full px-4 py-3 text-lg border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                autoFocus
              />
              {/* 单价取整复选框 */}
              <label className="flex items-center mt-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={smartPricingRoundPrice}
                  onChange={(e) => setSmartPricingRoundPrice(e.target.checked)}
                  className="w-4 h-4 text-amber-500 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-600">单价取整（单价保留整数，如 1000.00）</span>
              </label>
              {smartPricingAmount && parseFloat(smartPricingAmount) > 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  将为 <span className="font-semibold text-gray-700">{data.items.filter(i => i.quantity > 0).length}</span> 个分项自动分配单价，
                  总计 <span className="font-semibold text-amber-600">{formatAmount(parseFloat(smartPricingAmount))}</span>
                </p>
              )}
              {data.maxTotalPrice && parseFloat(smartPricingAmount || '0') > data.maxTotalPrice && (
                <p className="mt-2 text-sm text-red-500 flex items-center">
                  <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                  超出最高限价 {formatAmount(data.maxTotalPrice)}
                </p>
              )}
            </div>
            {/* 弹窗底部 */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowSmartPricing(false)}
                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={executeSmartPricing}
                disabled={!smartPricingAmount || parseFloat(smartPricingAmount) <= 0}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                确认分配
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 备注 */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          备注说明
        </label>
        <textarea
          value={data.notes}
          onChange={(e) => updateSetting('notes', e.target.value)}
          disabled={readOnly}
          rows={3}
          placeholder="添加报价备注说明..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
        />
      </div>
    </div>
  );
};

export default QuotationEditor;
